import { RankedBars } from '../components/Charts.tsx';
import { DataTable } from '../components/DataTable.tsx';
import { ItemLabel } from '../components/ItemLabel.tsx';
import { Card, Empty, Notice, Spinner } from '../components/Ui.tsx';
import { api } from '../lib/api.ts';
import { useAsync } from '../lib/auth.tsx';
import { money, moneyShort, number } from '../lib/format.ts';
import { ErrorState } from './Upgrade.tsx';

export function OpportunitiesPage() {
  const opportunities = useAsync(() => api.insights.opportunities(15), []);

  if (opportunities.loading) return <Spinner />;
  if (opportunities.error) return <ErrorState error={opportunities.error} />;

  const findings = opportunities.data!.findings;

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Opportunity finder</h1>
          <p>
            Items the subscriber network buys heavily but few shops supply, scored on revenue per
            existing seller rather than raw units - the question is how much money you would be
            walking into, not how many blocks change hands.
          </p>
        </div>
      </header>

      {findings.length === 0 ? (
        <Empty title="No clear gaps right now">
          <span className="muted">
            Either you already cover the thin categories, or the network needs more sellers before
            the signal is trustworthy.
          </span>
        </Empty>
      ) : (
        <>
          <Card title="Ranked opportunities" subtitle="Scored 0-100 against the best gap in the set">
            <RankedBars
              data={findings.slice(0, 10).map((f) => ({
                name: f.displayName,
                value: f.score,
                hint: f.rationale,
              }))}
              valueLabel="Opportunity score"
              formatValue={(value) => value.toFixed(1)}
            />
          </Card>

          <Card title="The detail">
            <DataTable
              rows={findings}
              rowKey={(f) => f.itemCode}
              initialSort={{ key: 'score', dir: 'desc' }}
              searchPlaceholder="Search items or categories"
              pageSize={25}
              columns={[
                {
                  key: 'item',
                  header: 'Item',
                  searchText: (f) => `${f.displayName} ${f.itemCode}`,
                  sortValue: (f) => f.displayName,
                  render: (f) => (
            <ItemLabel
              itemKey={f.itemCode}
              material={f.itemCode}
              displayName={f.displayName}
              size={20}
            />
          ),
                },
                {
                  key: 'category',
                  header: 'Category',
                  searchText: (f) => f.category,
                  sortValue: (f) => f.category,
                  render: (f) => <span className="muted">{f.category}</span>,
                },
                {
                  key: 'score',
                  header: 'Score',
                  align: 'num',
                  sortValue: (f) => f.score,
                  render: (f) => <strong>{f.score.toFixed(1)}</strong>,
                },
                {
                  key: 'units',
                  header: 'Network units',
                  align: 'num',
                  sortValue: (f) => f.networkUnits30d,
                  render: (f) => number(f.networkUnits30d),
                },
                {
                  key: 'revenue',
                  header: 'Network revenue',
                  align: 'num',
                  sortValue: (f) => f.networkRevenue30d,
                  render: (f) => moneyShort(f.networkRevenue30d),
                },
                {
                  key: 'sellers',
                  header: 'Sellers',
                  align: 'num',
                  sortValue: (f) => f.sellerCount,
                  render: (f) => number(f.sellerCount),
                },
                {
                  key: 'median',
                  header: 'Median price',
                  align: 'num',
                  sortValue: (f) => f.medianPrice,
                  render: (f) => money(f.medianPrice),
                },
                {
                  key: 'why',
                  header: 'Why',
                  searchText: (f) => f.rationale,
                  render: (f) => (
                    <span className="small muted" style={{ display: 'block', maxWidth: 360 }}>
                      {f.rationale}
                    </span>
                  ),
                },
              ]}
            />
          </Card>
        </>
      )}

      <div style={{ marginTop: 16 }}>
        <Notice>
          <strong>Read this as a lead, not a verdict.</strong> The score measures demand and thin
          supply inside our subscriber network. An item nobody in the network sells might still be
          well served by shops that have not joined - check <code>/find</code> before committing
          capital.
        </Notice>
      </div>
    </>
  );
}
